import axios from 'axios';

const BRAND_ID = Number(process.env.VUE_APP_BRAND_ID);
const BRAND_SLUG = process.env.VUE_APP_BRAND_SLUG;

const trimSlash = (value) => String(value || '').trim().replace(/\/+$/, '');

const expandEnv = (value) => {
  let s = String(value || '').trim();
  s = s.replace(/\$\{([A-Z0-9_]+)\}/g, (_, key) => String(process.env[key] || ''));
  return s.trim();
};

const resolveBackendUrl = () => {
  let url = trimSlash(expandEnv(process.env.VUE_APP_BACKEND_URL));
  if (/^https?:\/\//i.test(url) && !url.includes('${')) {
    return url;
  }
  const method = expandEnv(process.env.VUE_APP_BACKEND_METHOD) || 'http';
  const host = url.replace(/^https?:\/\//i, '').replace(/^:?\/\//, '').replace(/\$\{[^}]+\}/g, '');
  if (host) {
    return trimSlash(`${method}://${host.replace(/^\/+/, '')}`);
  }
  return '';
};

const apiBase = () => {
  const api = trimSlash(expandEnv(process.env.VUE_APP_BACKEND_URL_API));
  const backend = resolveBackendUrl();

  if (/^https?:\/\//i.test(api)) {
    return api;
  }
  if (backend) {
    const path = api ? (api.startsWith('/') ? api : `/${api}`) : '/api';
    return `${backend}${path}`;
  }
  return api || '/api';
};

const mediaBase = () => resolveBackendUrl();

const mediaUrl = (path) => {
  if (!path) return '';
  const s = String(path);
  if (/^https?:\/\//i.test(s) || s.startsWith('//')) return s;
  const normalized = s.startsWith('/') ? s : `/${s}`;
  const backend = mediaBase();
  return backend ? `${backend}${normalized}` : normalized;
};

const unwrapList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
};

const fetchApi = async (path, { method = 'GET', params, body } = {}) => {
  const search = new URLSearchParams();
  if (params && typeof params === 'object') {
    for (const [key, value] of Object.entries(params)) {
      if (value !== null && value !== undefined && value !== '') {
        search.set(key, String(value));
      }
    }
  }

  const query = search.toString();
  const url = `${apiBase()}/${String(path).replace(/^\//, '')}${query ? `?${query}` : ''}`;

  try {
    const response = await axios({
      url,
      method,
      data: body,
      headers: {
        Accept: 'application/json',
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
    });
    return response.data;
  } catch (e) {
    const data = e?.response?.data;
    if (data && typeof data === 'object') {
      return { success: false, ...data };
    }
    console.log('We have the error', e);
    return null;
  }
};

const fetchData = async (path, params) => unwrapList(await fetchApi(path, { params }));

const IMAGE_FOLDERS = {
  product: `${BRAND_SLUG}/product`,
  products: `${BRAND_SLUG}/product`,
  accessories: `${BRAND_SLUG}/product`,
  packages: 'product_categories_image',
  composition: `${BRAND_SLUG}/materials`,
  slider: `${BRAND_SLUG}/content/cover`,
};

const getStaticImage = (type, name, state = false, type2 = null) => {
  if (!type && !name) return '';
  if (typeof type === 'string' && (type.startsWith('http') || type.startsWith('//') || type.startsWith('/'))) {
    return mediaUrl(type);
  }
  if (typeof name === 'string' && (name.startsWith('http') || name.startsWith('//') || name.startsWith('/'))) {
    return mediaUrl(name);
  }
  const folderKey = state && type2 ? type2 : type;
  if (!name) return mediaUrl(folderKey);
  const folder = IMAGE_FOLDERS[folderKey] || folderKey;
  return mediaUrl(`/${folder}/${name}`);
};

let catalogCache = null;

const fetchCatalog = async (force = false) => {
  if (catalogCache && !force) return catalogCache;
  const payload = await fetchApi('products/public/catalog', {
    params: { brand_id: BRAND_ID, brand_slug: BRAND_SLUG },
  });
  catalogCache = payload && payload.data ? payload.data : payload;
  return catalogCache || { packages: [], package_types: [], features: [], items: [], accessories: [] };
};

const isObjectEmpty = (obj) => {
  for (const i in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, i)) {
      return false;
    }
  }
  return true;
};

const getRequireJson = (filename) => {
  try {
    return require('./assets/data/' + filename);
  } catch (e) {
    console.log('We have the error', e);
  }
  return null;
};

export default function () {
  return {
    BRAND_ID,
    BRAND_SLUG,
    apiBase,
    fetchApi,
    fetchData,
    fetchCatalog,
    getStaticImage,
    mediaUrl,
    isObjectEmpty,
    getRequireJson,
  };
}
